import classNames from "classnames";
import DailyLimitReached from "components/DailyLimitReached";
import Popover from "components/Popover";
import SignIn from "components/SignIn";
import OutOfCredits from "features/Subscription/components/OutOfCredits";
import SpendCreditOnSnapshot from "features/Subscription/components/SpendCreditOnSnapshot";
import { Badges } from "helpers/api";
import {
  countNonEmptySnapshotFields,
  displayFieldList,
  formatStudySnapshotValue,
  StudySnapshotFieldNameType,
  StudySnapshotFields,
} from "helpers/studyDetail";
import useLabels from "hooks/useLabels";
import { CreditedFeature } from "store/slices/subscription";
import StudySnapshotError from "./components/StudySnapshotError";
import StudySnapshotLoading from "./components/StudySnapshotLoading";
import StudySnapshotLegacyDesign from "./components/StudySnapshotLegacyDesign";
import useStudySnapshotFields from "./hooks/useStudySnapshotFields";
import useSubscription from "features/User/hooks/useSubscription";

export interface StudySnapshotProps {
  paperId: string;
  badges?: Badges;
  isTest?: boolean;
  fetchOnMount?: boolean;
  keyTakeaway?: string;
  legacyDesign?: boolean;
}

interface StudySnapshotFieldDetail {
  value: string | number | string[] | null;
  label: string;
  icon?: string;
}

type DetailedStudySnapshotFields = {
  [K in keyof StudySnapshotFields]: StudySnapshotFieldDetail;
};
const keyTakeawayFieldDetails = {
  id: "keyTakeaway",
  label: "Key takeaway",
  icon: "icon-text-quote",
};
export const fieldList = [
  [{ id: "population", label: "Population", icon: "icon-users-round" }],
  [
    { id: "sampleSize", label: "Sample size", icon: "icon-calculator" },
    { id: "studyCount", label: "Study count", icon: "icon-calculator" },
  ],
  [{ id: "duration", label: "Duration", icon: "icon-timer" }],
  [{ id: "country", label: "Location", icon: "icon-map-pin" }],
  [{ id: "methods", label: "Methods", icon: "icon-microscope" }],
  [{ id: "outcomes", label: "Outcomes", icon: "icon-scale" }],
  [{ id: "results", label: "Results", icon: "icon-file-check-2" }],
];

export const TOTAL_PAPER_FIELD_COUNT = fieldList.length;

const PartialFieldTooltip = ({
  availableFields,
}: {
  availableFields: Partial<Record<keyof StudySnapshotFields, any>>;
}) => {
  const [tooltipsLabels] = useLabels("tooltips.study-snapshot-partial-fields");
  const availableFieldKeys = Object.keys(availableFields).filter(
    (key) => (key as StudySnapshotFieldNameType) !== "keyTakeaway"
  );
  const availableFieldCount = availableFieldKeys.length;
  const nonAvailableFields = fieldList.reduce((acc, itemGroup) => {
    for (const item of itemGroup) {
      if (availableFieldKeys.includes(item.id)) {
        return acc;
      }
    }
    if (itemGroup.length) {
      acc.push(itemGroup[0]);
    }
    return acc;
  }, []);

  return (
    <div>
      <h3 className="flex gap-2 items-center">
        <i className="text-xl icon-check text-chart-emphasis" />
        <span className="sm-md text-fg-base">
          {tooltipsLabels["extracted-title"].replace(
            "{n}",
            availableFieldCount
          )}{" "}
        </span>
      </h3>
      <p className="mb-3 tiny-normal text-fg-muted">
        {availableFieldKeys
          .map(
            (fieldName) =>
              availableFields[fieldName as StudySnapshotFieldNameType].label
          )
          .join(", ")}
      </p>
      <h4 className="flex gap-2 items-center">
        <i className="text-xl icon-minus text-fg-muted" />
        <span className="sm-md text-fg-base">
          {tooltipsLabels["non-extracted-title"].replace(
            "{n}",
            nonAvailableFields.length
          )}
        </span>
      </h4>
      <p className="tiny-normal text-fg-muted">
        <span>{tooltipsLabels["non-extracted-desc"]}&nbsp;</span>
        <span>{nonAvailableFields.map((item) => item.label).join(", ")}</span>
      </p>
    </div>
  );
};

const sampleFields: { id: keyof StudySnapshotFields; value: string }[] = [
  { id: "population", value: "Older adults (50-71 years)" },
  { id: "sampleSize", value: "24" },
  { id: "methods", value: "Observational" },
  { id: "outcomes", value: "Body Mass Index projections" },
  {
    id: "results",
    value: "Social networks mitigate obesity in older groups.",
  },
];

const blurStateFields: DetailedStudySnapshotFields = sampleFields.reduce(
  (acc, field) => {
    fieldList.forEach((itemGroup) => {
      for (const item of itemGroup) {
        if (item.id === field.id) {
          acc[field.id] = {
            value: field.value,
            label: item.label,
            icon: item.icon,
          };
          return;
        }
      }
    });
    return acc;
  },
  {} as DetailedStudySnapshotFields
);

function StudySnapshot({
  paperId,
  badges,
  isTest,
  fetchOnMount = true,
  keyTakeaway,
  legacyDesign = false,
}: StudySnapshotProps) {
  const [studySnapshotLabels] = useLabels("study-snapshot");
  const { isFreePlan } = useSubscription();

  const {
    fields,
    isNotSignedIn,
    isOutOfCredits,
    fieldsError,
    isLoading,
    handleGenerateSnapshot,
    dailyLimitReached,
  } = useStudySnapshotFields({
    paperId,
    badges,
    fetchOnMount: !isFreePlan,
    isTest,
  });

  // TODO(Jimmy): Remove this option and StudySnapshotLegacyDesign import once the new design is stable
  // Will need to migrate details page once paper sidebar drawer is out and stable.
  if (legacyDesign) {
    return (
      <StudySnapshotLegacyDesign
        paperId={paperId}
        badges={badges}
        isTest={isTest}
        fetchOnMount={fetchOnMount}
        keyTakeaway={keyTakeaway}
      />
    );
  }

  const isBlurred = !fields;

  let availableFields = isBlurred
    ? blurStateFields
    : fieldList.reduce<
        Record<keyof StudySnapshotFields, StudySnapshotFieldDetail>
      >(
        (acc, itemGroup) => {
          for (const item of itemGroup) {
            if (fields[item.id as StudySnapshotFieldNameType]) {
              acc[item.id as StudySnapshotFieldNameType] = {
                value:
                  formatStudySnapshotValue(
                    item.id,
                    fields[item.id as StudySnapshotFieldNameType]
                  ) || null,
                label: item.label,
                icon: item.icon,
              };
              break;
            }
          }
          return acc;
        },
        {} as Record<keyof StudySnapshotFields, StudySnapshotFieldDetail>
      );

  if (keyTakeaway) {
    availableFields = {
      keyTakeaway: { ...keyTakeawayFieldDetails, value: keyTakeaway },
      ...availableFields,
    };
  }

  const availableFieldCount = fields
    ? countNonEmptySnapshotFields(fields as StudySnapshotFields)
    : 0;

  if (fieldsError) {
    return <StudySnapshotError error={fieldsError as Error} />;
  }

  if (isLoading) {
    return <StudySnapshotLoading />;
  }

  if (dailyLimitReached) {
    return (
      <div className="py-3 mx-4">
        <DailyLimitReached
          title={studySnapshotLabels["daily-limit-reached-error-title"]}
          description={studySnapshotLabels["daily-limit-reached-error-desc"]}
          showSupportEmail
        />
      </div>
    );
  }

  return (
    <div data-testid="study-snapshot-result" className="flex flex-col gap-4">
      {isOutOfCredits && isNotSignedIn ? (
        <SignIn
          feature={CreditedFeature.STUDY_SNAPSHOT}
          containerClassName="bg-bg-faint rounded-xl p-4"
        />
      ) : isOutOfCredits ? (
        <OutOfCredits
          feature={CreditedFeature.STUDY_SNAPSHOT}
          preventEventDefault
          stopEventPropagation
          containerClassName="bg-bg-faint rounded-xl p-4"
        />
      ) : isBlurred ? (
        <div className="p-4 rounded-xl bg-bg-faint">
          <SpendCreditOnSnapshot onUseCreditClick={handleGenerateSnapshot} />
        </div>
      ) : null}

      <ul className="flex flex-col">
        {Object.keys(availableFields).map((fieldName) => (
          <li className="flex gap-4 items-center min-h-10" key={fieldName}>
            <div className="flex gap-2 h-full w-[116px]">
              <i
                className={
                  availableFields[fieldName as StudySnapshotFieldNameType]
                    ?.icon + " shrink-0 size-4 text-fg-muted"
                }
              />
              <div className="font-medium text-fg-muted text-[13px] whitespace-nowrap">
                {
                  availableFields[fieldName as StudySnapshotFieldNameType]
                    ?.label
                }
              </div>
            </div>
            <div className="flex-1 flex gap-2.5">
              <div
                className={classNames(
                  "flex-1 sm-normal text-fg-base",
                  isBlurred &&
                    (fieldName as StudySnapshotFieldNameType) !==
                      "keyTakeaway" &&
                    "select-none blur-xs overflow-hidden"
                )}
              >
                {displayFieldList(
                  availableFields[fieldName as StudySnapshotFieldNameType]
                    ?.value
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>

      {!isBlurred && (
        <p
          className="flex items-center px-3 h-10 tiny-normal text-fg-muted"
          data-testid="fields-summary-section"
        >
          {availableFieldCount === TOTAL_PAPER_FIELD_COUNT ? (
            studySnapshotLabels["snapshot-all-results-subtext"]
          ) : (
            <Popover
              interactive
              appendToParent
              maxWidth={334}
              tooltipContent={
                <PartialFieldTooltip availableFields={availableFields} />
              }
            >
              <span data-testid="partial-attributes-tooltip">
                <span>{studySnapshotLabels["extracted"]}</span>
                <span
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  className="ml-1 underline"
                >
                  {studySnapshotLabels["n-attributes"].replace(
                    "{n-ratio}",
                    `${availableFieldCount}/${TOTAL_PAPER_FIELD_COUNT}`
                  )}
                </span>
              </span>
            </Popover>
          )}
        </p>
      )}
    </div>
  );
}

export default StudySnapshot;
